import { useState } from "react";

import { trackEvent } from "@/lib/analytics";
import { categories, type ProductCategory } from "@/data/products";
import { ChevronRight, Check, ChevronDown } from "lucide-react";

interface ProductGridProps {
  onSelect: (product: ProductCategory) => void;
}

/** Top-5 displayed first; remaining shown after explicit user action.
 *  Reduces choice overload and matches the funnel plan. */
const TOP_IDS = ["daynight", "roller", "plisse", "net-hinged", "rolstavni"];

const ProductGrid = ({ onSelect }: ProductGridProps) => {
  const [expanded, setExpanded] = useState(false);
  const top = TOP_IDS.map((id) => categories.find((c) => c.id === id)).filter(Boolean) as ProductCategory[];
  const rest = categories.filter((c) => !TOP_IDS.includes(c.id));
  const visible = expanded ? [...top, ...rest] : top;

  return (
  <section className="px-4 md:px-0 py-1 md:py-2">
    <div className="text-center mb-4 md:mb-6">
      <span className="hidden md:inline-block mb-2 px-3 py-1 rounded-full bg-muted text-[10px] font-semibold text-muted-foreground tracking-[0.14em] uppercase">
        Каталог
      </span>
      <h2 className="text-[20px] md:text-3xl font-bold text-foreground tracking-tight">
        Популярные решения Zebra Jaluzi
      </h2>
      <p className="text-[12px] md:text-sm text-muted-foreground mt-1.5 max-w-2xl mx-auto">
        Подбираем жалюзи, сетки и рольставни под интерьер, окно, фасад и задачу клиента.
      </p>
    </div>
    <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 no-scrollbar md:grid md:grid-cols-5 md:overflow-visible md:pb-0">
      {visible.map((cat) => {
        const isTop = TOP_IDS.includes(cat.id);
        return (
        <button
          key={cat.id}
          onClick={() => {
            trackEvent("view_category", { category_id: cat.id, category_title: cat.title });
            onSelect(cat);
          }}
          className={`group relative flex w-[76vw] max-w-[270px] shrink-0 snap-start flex-col overflow-hidden rounded-lg bg-card text-left transition-all active:scale-[0.98] md:w-auto md:max-w-none md:hover:-translate-y-1 border ${
            isTop ? "border-primary/20 md:hover:border-primary/50" : "border-border/60 md:hover:border-primary/30"
          } md:hover:shadow-xl`}
          style={{ boxShadow: "var(--shadow-card)" }}
        >
          <div className="relative overflow-hidden">
            <img
              src={cat.image}
              alt={cat.alt || `${cat.title} — ${cat.category} в Ташкенте`}
              loading="lazy"
              decoding="async"
              width={640}
              height={512}
              className="w-full h-28 md:h-36 object-cover transition-transform duration-500 md:group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent opacity-60 md:opacity-50 md:group-hover:opacity-70 transition-opacity" />
            <span className="absolute top-2.5 left-2.5 md:top-3 md:left-3 px-2 py-0.5 md:px-2.5 md:py-1 rounded-full bg-card/90 backdrop-blur text-[9px] md:text-[10px] font-semibold text-foreground/80 uppercase tracking-wider">
              {cat.category}
            </span>
            {(cat.badge || isTop) && (
              <span className="absolute top-2.5 right-2.5 md:top-3 md:right-3 px-2 py-0.5 md:px-2.5 md:py-1 rounded-full bg-primary text-primary-foreground text-[9px] md:text-[10px] font-bold uppercase tracking-wider shadow-md">
                {cat.badge || "Хит"}
              </span>
            )}
            <h3 className="hidden md:block absolute bottom-2.5 left-3 right-3 text-[13px] font-semibold text-primary-foreground leading-snug tracking-tight drop-shadow-lg">
              {cat.title}
            </h3>
          </div>
          <div className="p-3 flex flex-col flex-1">
            <h3 className="md:hidden text-[13px] font-semibold text-foreground leading-snug tracking-tight">{cat.title}</h3>
            <p className="text-[11px] md:text-xs text-muted-foreground mt-1 line-clamp-1 leading-relaxed flex-1">{cat.description}</p>
            {cat.priceHint && (
              <span className="mt-1.5 text-[12px] md:text-[13px] font-bold text-foreground">{cat.priceHint}</span>
            )}
            {cat.priceNote && (
              <span className="mt-0.5 text-[9px] md:text-[10px] text-muted-foreground leading-snug line-clamp-1">{cat.priceNote}</span>
            )}
            <div className="mt-1.5 flex items-center gap-1.5 text-[9px] md:text-[10px] text-muted-foreground">
              <Check className="h-3 w-3 text-primary" strokeWidth={2.5} />
              {cat.id === "rolstavni" ? "Замер и монтаж под ключ" : "Бесплатный замер и установка"}
            </div>
            <span className="mt-2 inline-flex items-center justify-between gap-1 text-[10px] md:text-[11px] font-semibold text-primary border-t border-border/60 pt-2 transition-all md:group-hover:gap-2">
              Подробнее <ChevronRight className="h-3.5 w-3.5 md:h-4 md:w-4 transition-transform md:group-hover:translate-x-0.5" />
            </span>
          </div>
        </button>
        );
      })}
    </div>

    {!expanded && rest.length > 0 && (
      <div className="mt-4 md:mt-5 text-center">
        <button
          onClick={() => {
            trackEvent("show_all_products");
            setExpanded(true);
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-[12px] font-semibold text-foreground active:scale-[0.97] md:hover:border-primary/50 md:hover:text-primary transition-all"
        >
          Другие варианты ({rest.length}) <ChevronDown className="h-4 w-4" />
        </button>
      </div>
    )}
  </section>
  );
};

export default ProductGrid;
